/**
 * Console Utilities
 * =================
 * Helper functions for virtual console launcher feature.
 */

import type { Asset, ConsoleType } from '../types'

/**
 * Console URL patterns for different management interfaces.
 */
const CONSOLE_URL_PATTERNS: Record<Exclude<ConsoleType, 'custom'>, string> = {
  idrac8: 'https://{ip}/console',
  idrac9: 'https://{ip}/restgui/start.html',
  supermicro: 'https://{ip}/cgi/url_redirect.cgi?url_name=man_ikvm_html5_bootstrap',
  ipmi: 'https://{ip}/',
}

/**
 * Get the console URL for an asset based on its console type.
 *
 * @param asset - The asset to get the console URL for.
 * @returns The console URL, or null if not configured.
 */
export function getConsoleUrl(asset: Asset): string | null {
  if (!asset.management_ip) {
    return null
  }

  if (!asset.console_type) {
    return null
  }

  // Custom type uses management_url directly
  if (asset.console_type === 'custom') {
    return asset.management_url
  }

  // Get pattern for this console type
  const pattern = CONSOLE_URL_PATTERNS[asset.console_type]
  if (!pattern) {
    return null
  }

  return pattern.replace('{ip}', asset.management_ip)
}

/**
 * Check if an asset supports console launch.
 *
 * @param asset - The asset to check.
 * @returns True if the asset can launch a console.
 */
export function supportsConsole(asset: Asset): boolean {
  return getConsoleUrl(asset) !== null
}

/**
 * Open a console window for an asset.
 *
 * @param asset - The asset to open the console for.
 * @returns The opened window, or null if failed.
 */
export function openConsole(asset: Asset): Window | null {
  const url = getConsoleUrl(asset)
  if (!url) {
    console.warn(`Cannot open console for asset ${asset.asset_id}: no console URL`)
    return null
  }

  // Open in a new window with specific dimensions
  // Using unique window name per asset so reopening focuses existing window
  const windowName = `exodus_console_${asset.id}`
  const windowFeatures = [
    'width=1280',
    'height=800',
    'menubar=no',
    'toolbar=no',
    'location=no',
    'status=no',
    'resizable=yes',
    'scrollbars=yes',
  ].join(',')

  const win = window.open(url, windowName, windowFeatures)

  if (win) {
    win.focus()
  }

  return win
}

/**
 * Auto-detect console type from manufacturer and model.
 *
 * @param manufacturerName - The manufacturer name (e.g., "Dell Inc.")
 * @param model - The model name (e.g., "PowerEdge R740")
 * @returns Detected console type or null.
 */
export function detectConsoleType(
  manufacturerName: string | null | undefined,
  model: string | null | undefined
): ConsoleType | null {
  if (!manufacturerName) {
    return null
  }

  const manufacturerLower = manufacturerName.toLowerCase()

  // Dell servers use iDRAC
  if (manufacturerLower.includes('dell')) {
    if (model) {
      const modelLower = model.toLowerCase()
      // R740, R640, R540, T640, etc. use iDRAC 9
      const idrac9Models = ['r740', 'r640', 'r540', 't640', 'r440', 'r340', 'r940', 'c6525']
      if (idrac9Models.some(m => modelLower.includes(m))) {
        return 'idrac9'
      }
      // R730, R630, R530, etc. use iDRAC 8
      const idrac8Models = ['r730', 'r630', 'r530', 't630', 'r430', 'r330', 'r930']
      if (idrac8Models.some(m => modelLower.includes(m))) {
        return 'idrac8'
      }
    }
    // Default to iDRAC 9 for unknown Dell models
    return 'idrac9'
  }

  // Supermicro servers
  if (manufacturerLower.includes('supermicro')) {
    return 'supermicro'
  }

  return null
}
